/**
 * Zip extraction + JSON parsing for ChatGPT export archives.
 * Uses JSZip (loaded from CDN in browser, npm in tests).
 *
 * @module parser
 */

/** @typedef {import('./types.js').ArchiveParseResult} ArchiveParseResult */
/** @typedef {import('./types.js').ProgressEvent} ProgressEvent */

/**
 * Parse a ChatGPT export zip archive and extract conversations.
 *
 * @param {File|ArrayBuffer|Uint8Array} archiveFile - The zip file to parse
 * @param {Object} [options]
 * @param {(event: ProgressEvent) => void} [options.onProgress] - Progress callback
 * @returns {Promise<ArchiveParseResult>}
 */
export async function parseArchive(archiveFile, options = {}) {
  const { onProgress } = options;
  const JSZip = globalThis.JSZip;

  if (!JSZip) {
    throw new Error('JSZip not loaded. Include JSZip via CDN or npm.');
  }

  // Get raw bytes + size
  let archiveSizeBytes;
  let data;
  if (archiveFile instanceof File) {
    archiveSizeBytes = archiveFile.size;
    data = await archiveFile.arrayBuffer();
  } else if (archiveFile instanceof ArrayBuffer) {
    archiveSizeBytes = archiveFile.byteLength;
    data = archiveFile;
  } else if (archiveFile instanceof Uint8Array) {
    archiveSizeBytes = archiveFile.byteLength;
    data = archiveFile;
  } else {
    throw new Error('archiveFile must be a File, ArrayBuffer, or Uint8Array');
  }

  // Unzip
  if (onProgress) onProgress({ stage: 'unzip', pct: 0 });

  let zip;
  try {
    zip = await JSZip.loadAsync(data);
  } catch {
    throw new Error('Not a valid zip file.');
  }

  if (onProgress) onProgress({ stage: 'unzip', pct: 50 });

  const filesFound = Object.keys(zip.files);

  // Find conversation files — single conversations.json or split conversations-NNN.json
  let convFiles = [];

  // Try single file first (at root or one level deep)
  let singleFile = zip.file('conversations.json');
  if (!singleFile) {
    for (const path of filesFound) {
      const parts = path.split('/');
      if (parts.length === 2 && parts[1] === 'conversations.json') {
        singleFile = zip.file(path);
        break;
      }
    }
  }

  if (singleFile) {
    convFiles = [singleFile];
  } else {
    // Look for split format: conversations-000.json, conversations-001.json, ...
    const splitPattern = /(?:^|\/)(conversations-\d{3}\.json)$/;
    const matches = [];
    for (const path of filesFound) {
      if (splitPattern.test(path)) {
        matches.push(path);
      }
    }
    matches.sort();
    convFiles = matches.map(p => zip.file(p));
  }

  if (convFiles.length === 0) {
    throw new Error(
      'conversations.json not found in archive. ' +
      'Make sure this is a ChatGPT data export zip.'
    );
  }

  if (onProgress) onProgress({ stage: 'unzip', pct: 100 });

  // Parse JSON from all conversation files
  if (onProgress) onProgress({ stage: 'parse', pct: 0 });

  let conversations = [];
  for (let i = 0; i < convFiles.length; i++) {
    const jsonText = await convFiles[i].async('string');
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      throw new Error(
        `${convFiles[i].name} contains invalid JSON. The export file may be corrupted.`
      );
    }

    // Extract array from parsed data
    let chunk;
    if (Array.isArray(parsed)) {
      chunk = parsed;
    } else if (
      typeof parsed === 'object' &&
      parsed !== null &&
      Array.isArray(parsed.conversations)
    ) {
      chunk = parsed.conversations;
    } else {
      throw new Error(
        `${convFiles[i].name} does not contain a valid conversation array.`
      );
    }
    conversations = conversations.concat(chunk);

    if (onProgress) {
      const detail = convFiles.length > 1 ? `Parsing file ${i + 1} of ${convFiles.length}` : undefined;
      onProgress({ stage: 'parse', pct: Math.round(((i + 1) / convFiles.length) * 100), detail });
    }
  }

  return { conversations, archiveSizeBytes, filesFound };
}
